import { useCallback, useEffect, useState } from 'react';
import { Button, Modal, Slider } from 'antd';
import Cropper from 'react-easy-crop';
import type { Area } from 'react-easy-crop';
import { getCroppedBlob, type PixelCrop } from './cropImage';

// Sovereign Emerald palette
const SE = {
  paper:         '#FAF7F0',
  title:         '#0B3B2E',
  bottle:        '#1F5A47',
  champagneRing: 'rgba(201,169,97,0.45)',
  hairline:      'rgba(11,59,46,0.14)',
  muted:         '#7C8A82',
};

interface Props {
  file: File | null;
  open: boolean;
  onCancel: () => void;
  /** Uploads the cropped blob. Handles its own errors; the modal only tracks the saving state. */
  onSave: (blob: Blob) => Promise<void>;
}

/** WhatsApp-style adjuster: drag to pan, slider/scroll to zoom, 90° rotate, circular mask. */
export default function PhotoAdjustModal({ file, open, onCancel, onSave }: Props) {
  const [imageSrc, setImageSrc] = useState<string | null>(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [areaPixels, setAreaPixels] = useState<PixelCrop | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [loadError, setLoadError] = useState(false);

  // Object URL lifecycle + per-file state reset
  useEffect(() => {
    if (!file) {
      setImageSrc(null);
      return;
    }
    const url = URL.createObjectURL(file);
    setImageSrc(url);
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    setRotation(0);
    setAreaPixels(null);
    setLoadError(false);
    // Pre-flight decode so a corrupt file shows the error state instead of a blank cropper
    const probe = new Image();
    probe.onerror = () => setLoadError(true);
    probe.src = url;
    return () => URL.revokeObjectURL(url);
  }, [file]);

  useEffect(
    () => () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
    },
    [previewUrl],
  );

  const onCropComplete = useCallback((_area: Area, pixels: Area) => setAreaPixels(pixels), []);

  // Live round preview — regenerated when the user finishes a gesture
  useEffect(() => {
    if (!imageSrc || !areaPixels || loadError) return;
    let cancelled = false;
    getCroppedBlob(imageSrc, areaPixels, rotation, 96)
      .then((blob) => {
        if (cancelled) return;
        setPreviewUrl((prev) => {
          if (prev) URL.revokeObjectURL(prev);
          return URL.createObjectURL(blob);
        });
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
    // zoom is intentionally omitted: Cropper reports fresh areaPixels after every zoom gesture
  }, [imageSrc, areaPixels, rotation, loadError]);

  const handleSave = async () => {
    if (!imageSrc || !areaPixels || saving) return;
    setSaving(true);
    try {
      const blob = await getCroppedBlob(imageSrc, areaPixels, rotation);
      await onSave(blob);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open={open}
      onCancel={saving ? undefined : onCancel}
      footer={null}
      centered
      width={440}
      destroyOnHidden
      maskClosable={!saving}
      closable={!saving}
      title={<span style={{ color: SE.title, fontWeight: 700 }}>Adjust profile photo</span>}
      styles={{
        body: { backgroundColor: SE.paper },
        header: { backgroundColor: SE.paper, borderBottom: `1px solid ${SE.hairline}`, paddingBottom: 10 },
      }}
    >
      {loadError ? (
        <div className="py-10 text-center">
          <p className="text-sm" style={{ color: SE.muted }}>Could not load this image. It may be corrupt or unsupported.</p>
          <Button className="mt-4" onClick={onCancel}>Choose another file</Button>
        </div>
      ) : (
        <>
          <div className="relative h-[320px] w-full overflow-hidden rounded-xl" style={{ backgroundColor: '#101614' }}>
            {imageSrc && (
              <Cropper
                image={imageSrc}
                crop={crop}
                zoom={zoom}
                rotation={rotation}
                aspect={1}
                cropShape="round"
                showGrid={false}
                minZoom={1}
                maxZoom={3}
                zoomSpeed={0.2}
                onCropChange={setCrop}
                onZoomChange={setZoom}
                onCropComplete={onCropComplete}
              />
            )}
          </div>

          <div className="mt-4 flex items-center gap-3">
            <svg className="h-4 w-4 flex-shrink-0" fill="none" stroke={SE.muted} viewBox="0 0 24 24">
              <circle cx="11" cy="11" r="7" strokeWidth="1.8" />
              <path strokeLinecap="round" strokeWidth="1.8" d="M21 21l-4.35-4.35M8 11h6" />
            </svg>
            <Slider
              className="flex-1"
              min={1}
              max={3}
              step={0.05}
              value={zoom}
              onChange={(v: number) => setZoom(v)}
              tooltip={{ open: false }}
            />
            <svg className="h-5 w-5 flex-shrink-0" fill="none" stroke={SE.muted} viewBox="0 0 24 24">
              <circle cx="11" cy="11" r="7" strokeWidth="1.8" />
              <path strokeLinecap="round" strokeWidth="1.8" d="M21 21l-4.35-4.35M11 8v6M8 11h6" />
            </svg>
            <Button size="small" onClick={() => setRotation((r) => (r + 90) % 360)}>
              Rotate 90°
            </Button>
            {previewUrl && (
              <img
                src={previewUrl}
                alt="Cropped preview"
                className="h-12 w-12 flex-shrink-0 rounded-full object-cover"
                style={{ boxShadow: `0 0 0 2px ${SE.champagneRing}` }}
              />
            )}
          </div>

          <div className="mt-5 flex justify-end gap-2">
            <Button onClick={onCancel} disabled={saving}>Cancel</Button>
            <Button
              type="primary"
              onClick={handleSave}
              loading={saving}
              disabled={!areaPixels}
              style={{ backgroundColor: SE.bottle }}
            >
              Save photo
            </Button>
          </div>
        </>
      )}
    </Modal>
  );
}
